import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Trash2, Eye, Check, Send, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('');

  const selectedTemplate = useMemo(
    () => templateOptions.find((template) => template.key === selectedTemplateKey) || null,
    [templateOptions, selectedTemplateKey]
  );

  const loadMessages = useCallback(async () => {
    try {
      const data = await adminApi.getMessages();
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const loadTemplates = useCallback(async (messageId) => {
    setLoadingTemplates(true);
    try {
      const templates = await adminApi.getMessageTemplates(messageId);
      setTemplateOptions(templates || []);
      if ((templates || []).length > 0) {
        setSelectedTemplateKey(templates[0].key);
      } else {
        setSelectedTemplateKey('');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplateOptions([]);
      setSelectedTemplateKey('');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const handleMarkRead = async (messageId) => {
    try {
      await adminApi.markRead(messageId);
      setMessages((prev) => prev.map((m) => (
        m.id === messageId ? { ...m, read: true } : m
      )));
      setSelectedMessage((prev) => (prev?.id === messageId ? { ...prev, read: true } : prev));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;

    try {
      await adminApi.deleteMessage(messageId);
      toast.success('Message supprimé');
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
        setReplySubject('');
        setReplyMessage('');
        setTemplateOptions([]);
        setSelectedTemplateKey('');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openMessage = useCallback((message) => {
    setSelectedMessage(message);
    setReplySubject(`Re: ${message.subject}`);
    setReplyMessage('');
    setTemplateOptions([]);
    setSelectedTemplateKey('');
    loadTemplates(message.id);
    if (!message.read) {
      handleMarkRead(message.id);
    }
  }, [loadTemplates]);

  const applyTemplate = useCallback((templateKey) => {
    const template = templateOptions.find((item) => item.key === templateKey);
    if (!template) return;
    setReplySubject(template.subject);
    setReplyMessage(template.message);
  }, [templateOptions]);

  const handleSendReply = useCallback(async (templateKey = null, subjectOverride = null, messageOverride = null) => {
    if (!selectedMessage) return;

    const subjectValue = (subjectOverride ?? replySubject).trim();
    const messageValue = (messageOverride ?? replyMessage).trim();

    const payload = {
      subject: subjectValue,
      message: messageValue,
      template_key: templateKey || selectedTemplateKey || null
    };

    if (!payload.subject || !payload.message) {
      toast.error('Veuillez remplir le sujet et le message.');
      return;
    }

    setSendingReply(true);
    try {
      await adminApi.sendMessageReply(selectedMessage.id, payload);
      toast.success('Reponse envoyee avec succes.');

      const repliedAt = new Date().toISOString();
      setMessages((prev) => prev.map((m) => (
        m.id === selectedMessage.id
          ? {
            ...m,
            read: true,
            replied: true,
            replied_at: repliedAt,
            last_reply_subject: payload.subject
          }
          : m
      )));

      setSelectedMessage((prev) => prev ? {
        ...prev,
        read: true,
        replied: true,
        replied_at: repliedAt,
        last_reply_subject: payload.subject
      } : prev);

      setReplyMessage('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de l\'envoi du mail.');
    } finally {
      setSendingReply(false);
    }
  }, [replyMessage, replySubject, selectedMessage, selectedTemplateKey]);

  const handleSendAutomaticAck = useCallback(async () => {
    if (!selectedTemplate) {
      toast.error('Aucun template disponible.');
      return;
    }
    setReplySubject(selectedTemplate.subject);
    setReplyMessage(selectedTemplate.message);
    await handleSendReply(selectedTemplate.key, selectedTemplate.subject, selectedTemplate.message);
  }, [handleSendReply, selectedTemplate]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-medium tracking-tight">Messages</h1>
        <p className="text-muted-foreground mt-1">Messages reçus via le formulaire de contact</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Boîte de réception ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Aucun message pour le moment</p>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {messages.map((message) => (
                  <div 
                    key={message.id}
                    onClick={() => openMessage(message)}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedMessage?.id === message.id
                        ? 'border-primary bg-primary/5'
                        : message.read
                        ? 'border-border bg-secondary/30 hover:bg-secondary/50'
                        : 'border-primary/50 bg-primary/10 hover:bg-primary/15'
                    }`}
                    data-testid={`message-${message.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {!message.read && (
                            <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                          )}
                          <p className={`font-medium truncate ${!message.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {message.name}
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{message.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(message.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(message.id); }}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Detail */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Détail du message
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedMessage ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-serif text-xl">{selectedMessage.name}</p>
                    <p className="text-primary text-sm">{selectedMessage.email}</p>
                    {selectedMessage.phone && (
                      <p className="text-sm text-muted-foreground">{selectedMessage.phone}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {selectedMessage.read && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <Check className="h-3 w-3" /> Lu
                      </span>
                    )}
                    {selectedMessage.replied && (
                      <span className="text-xs text-primary">Repondu</span>
                    )}
                  </div>
                </div>
                
                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Sujet</p>
                  <p className="font-medium">{selectedMessage.subject}</p>
                </div>
                
                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-1">Message</p>
                  <p className="whitespace-pre-wrap text-muted-foreground">{selectedMessage.message}</p>
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Reçu le {format(new Date(selectedMessage.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </p>
                  {selectedMessage.replied_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Derniere reponse envoyee le {format(new Date(selectedMessage.replied_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                    </p>
                  )}
                </div>

                <div className="pt-4 border-t border-border space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">Repondre depuis la plateforme</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSendAutomaticAck}
                      disabled={sendingReply || loadingTemplates || !selectedTemplate}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Envoi auto
                    </Button>
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">Template</p>
                    <select
                      value={selectedTemplateKey}
                      onChange={(e) => {
                        const key = e.target.value;
                        setSelectedTemplateKey(key);
                        applyTemplate(key);
                      }}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      disabled={loadingTemplates || templateOptions.length === 0}
                    >
                      {templateOptions.length === 0 ? (
                        <option value="">Aucun template</option>
                      ) : (
                        templateOptions.map((template) => (
                          <option key={template.key} value={template.key}>{template.label}</option>
                        ))
                      )}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Choisissez un modele puis adaptez votre message avant envoi.
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">Sujet</p>
                    <Input
                      value={replySubject}
                      onChange={(e) => setReplySubject(e.target.value)}
                      placeholder="Objet de la reponse"
                    />
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">Votre reponse</p>
                    <div className="rounded-md border border-input bg-background px-3 py-2 text-sm space-y-1">
                      <p className="text-muted-foreground/60 select-none">Bonjour {selectedMessage.name},</p>
                      <Textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Ecrivez votre reponse ici..."
                        rows={5}
                        className="border-0 p-0 shadow-none focus-visible:ring-0 resize-none"
                      />
                      <p className="text-muted-foreground/60 select-none">Cordialement,<br />Eddy Dossou</p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    className="w-full"
                    onClick={() => handleSendReply(null)}
                    disabled={sendingReply}
                  >
                    {sendingReply ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Envoyer la reponse
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-12">
                Sélectionnez un message pour voir les détails
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
